import {
  DatabaseNotInitializedError,
  DatabaseInitializationError,
} from "@/domains/errors/kinds/database";
import { TimeoutError } from "p-timeout";
import { DatabaseConnectionManager } from "./database-connection-manager";
import { ElectricSyncManager } from "./electric-sync-manager";
import { SmartContextRepository } from "../repositories/smart-context-repository";
import { SmartViewRepository } from "../repositories/smart-view-repository";
import { SmartCardRepository } from "../repositories/smart-card-repository";
import { createRepositories } from "../repositories/registry";
import { DatabaseFilesystemKind } from "@/domains/database/types/db";
import pTimeout from "p-timeout";
import { DATABASE_CONFIG } from "@/domains/database/core/config";

export interface DatabaseInitOptions {
  spaceAccountId: string;
}

export enum DatabaseState {
  UNINITIALIZED = "uninitialized",
  INITIALIZED = "initialized",
}

/**
 * Main database manager that orchestrates all database operations.
 *
 * Features:
 * - Single database instance for the current space account
 * - Repository pattern for organized data access
 * - Electric sync integration for real-time updates
 */
export class DatabaseManager {
  // Core managers
  private connectionManager: DatabaseConnectionManager | null = null;
  private electricSyncManager: ElectricSyncManager | null = null;

  // State tracking
  private initialized = false;

  // Repository instances
  private _smartContexts!: SmartContextRepository;
  private _smartViews!: SmartViewRepository;
  private _smartCards!: SmartCardRepository;
  private _stats!: import("../repositories").StatsRepository;

  // Public repository getters that ensure initialization
  get smartContexts(): SmartContextRepository {
    this.checkInitialized();
    return this._smartContexts;
  }

  get smartViews(): SmartViewRepository {
    this.checkInitialized();
    return this._smartViews;
  }

  get smartCards(): SmartCardRepository {
    this.checkInitialized();
    return this._smartCards;
  }

  get stats() {
    this.checkInitialized();
    return this._stats;
  }

  private checkInitialized(): void {
    if (!this.initialized) {
      throw new DatabaseNotInitializedError({
        component: "DatabaseManager",
        action: "accessing repositories",
      });
    }
  }

  constructor() {}

  /**
   * Initialize the database for a specific space account.
   */
  async init(options: DatabaseInitOptions): Promise<void> {
    const { spaceAccountId } = options;

    // Skip if already initialized
    if (this.initialized) {
      return;
    }

    try {
      const initializationPromise = this.performInit(spaceAccountId);

      await pTimeout(initializationPromise, {
        milliseconds: DATABASE_CONFIG.INITIALIZATION_TIMEOUT_MS,
        message: "Database initialization timed out.",
      });
    } catch (error) {
      if (error instanceof TimeoutError) {
        throw new DatabaseInitializationError(error.message);
      }

      throw error;
    }
  }

  /**
   * Performs the actual database initialization logic.
   */
  private async performInit(spaceAccountId: string): Promise<void> {
    // Get connection manager instance
    // NOTE: We are currently only using the in-memory filesystem.
    // In the future, we may roll out a persistent db-backed filesystem.
    this.connectionManager = DatabaseConnectionManager.getInstance({
      spaceAccountId,
      connectionOptions: { filesystemKind: DatabaseFilesystemKind.InMemory },
    });

    // Initialize repositories
    this.initializeRepositories();

    // Initialize the database connection
    await this.connectionManager.init();

    // Ensure sync manager and start Electric sync
    this.ensureSyncManager();
    await this.startSync();

    this.initialized = true;
  }

  /**
   * Clean up resources and prepare for shutdown.
   * Does not close the database connection (managed by ConnectionManager).
   */
  async cleanup(): Promise<void> {
    // Stop sync if running
    await this.stopSync();

    // Clear state
    this.initialized = false;
  }

  /**
   * Start Electric sync for real-time data synchronization.
   */
  async startSync(): Promise<void> {
    if (!this.electricSyncManager) {
      return; // No sync manager available
    }

    await this.electricSyncManager.startSync();
  }

  async stopSync(): Promise<void> {
    if (this.electricSyncManager) {
      await this.electricSyncManager.stopSync();
    }
  }

  /**
   * Get the current initialization state.
   */
  getInitializationState(): DatabaseState {
    return this.initialized ? DatabaseState.INITIALIZED : DatabaseState.UNINITIALIZED;
  }

  /**
   * Check if the database is initialized and ready for use.
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  getFilesystemKind(): DatabaseFilesystemKind | null {
    return this.connectionManager?.getFilesystemKind() ?? null;
  }

  private initializeRepositories(): void {
    if (!this.connectionManager) {
      throw new Error(
        "[DatabaseManager] Cannot initialize repositories without connection manager"
      );
    }

    const repos = createRepositories(this.connectionManager);
    this._smartContexts = repos.smartContexts;
    this._smartViews = repos.smartViews;
    this._smartCards = repos.smartCards;
    this._stats = repos.stats;
  }

  private ensureSyncManager(): void {
    if (!this.connectionManager) return;
    if (!this.electricSyncManager) {
      this.electricSyncManager = new ElectricSyncManager(this.connectionManager);
    }
  }
}
